import React, { useState } from 'react';

/**
 * SetupStepper Component
 *
 * Guided setup flow that collapses completed steps.
 * Turns passive status into actionable progress.
 *
 * Usage:
 * <SetupStepper
 *   steps={[
 *     { id: 'email', title: 'Connect Email', completed: true, onClick: () => {} },
 *     { id: 'calendar', title: 'Connect Calendar', completed: false, current: true, onClick: () => {} },
 *     { id: 'templates', title: 'Create Templates', completed: false, onClick: () => {} },
 *   ]}
 * />
 */

export const SetupStepper = ({
  steps = [],
  title = 'Setup Progress',
  description,
  onStepClick,
  showAllSteps = false,
  collapseCompleted = true,
}) => {
  const [expandedStep, setExpandedStep] = useState(null);

  const completedCount = steps.filter(s => s.completed).length;
  const totalCount = steps.length;
  const allComplete = completedCount === totalCount;
  const currentStep = steps.find(s => s.current) || steps.find(s => !s.completed);

  const handleStepClick = (step) => {
    if (step.onClick) {
      step.onClick();
    } else if (onStepClick) {
      onStepClick(step);
    }
    setExpandedStep(expandedStep === step.id ? null : step.id);
  };

  // If all complete, show condensed success state
  if (allComplete && collapseCompleted) {
    return (
      <div className="bg-green-500/10 border border-green-500/25 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-green-500/15 flex items-center justify-center">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h4 className="font-semibold text-green-300">{title}</h4>
            <p className="text-sm text-green-400">All {totalCount} steps completed</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-elevated border border-line rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-line-subtle bg-muted">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-semibold text-ink">{title}</h4>
            {description && (
              <p className="text-sm text-ink-subtle mt-0.5">{description}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-ink-muted">
              {completedCount}/{totalCount}
            </span>
            <div className="w-24 h-2 bg-subtle rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full transition-all duration-500"
                style={{ width: `${(completedCount / totalCount) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Steps */}
      <div className="divide-y divide-line-subtle">
        {steps.map((step, index) => {
          const isCompleted = step.completed;
          const isCurrent = step.current || (!isCompleted && index === steps.findIndex(s => !s.completed));
          const isDisabled = step.disabled || (!isCompleted && !isCurrent && !showAllSteps);
          const isExpanded = expandedStep === step.id;

          // Collapse completed steps if option enabled
          if (isCompleted && collapseCompleted && !isExpanded) {
            return (
              <button
                key={step.id}
                onClick={() => setExpandedStep(step.id)}
                className="w-full px-5 py-2 flex items-center gap-3 text-left hover:bg-muted transition-colors"
              >
                <div className="w-6 h-6 rounded-full bg-green-500/15 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-sm text-ink-subtle line-through">{step.title}</span>
                <svg className="w-4 h-4 text-ink-subtle ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            );
          }

          return (
            <div
              key={step.id}
              className={`px-5 py-4 ${isCurrent ? 'bg-accent-soft/50' : ''} ${isDisabled ? 'opacity-50' : ''}`}
            >
              <div className="flex items-start gap-4">
                {/* Step Number/Icon */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isCompleted
                    ? 'bg-green-500/15'
                    : isCurrent
                    ? 'bg-accent-soft'
                    : 'bg-subtle'
                }`}>
                  {isCompleted ? (
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <span className={`text-sm font-semibold ${isCurrent ? 'text-blue-600' : 'text-ink-subtle'}`}>
                      {index + 1}
                    </span>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <h5 className={`font-medium ${
                        isCompleted
                          ? 'text-ink-subtle line-through'
                          : isCurrent
                          ? 'text-blue-300'
                          : 'text-ink-muted'
                      }`}>
                        {step.title}
                      </h5>
                      {step.description && (
                        <p className={`text-sm mt-0.5 ${
                          isCurrent ? 'text-blue-400' : 'text-ink-subtle'
                        }`}>
                          {step.description}
                        </p>
                      )}
                    </div>

                    {/* Action */}
                    {!isCompleted && !isDisabled && (
                      <button
                        onClick={() => handleStepClick(step)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-shrink-0 ${
                          isCurrent
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-subtle text-ink-muted hover:bg-subtle'
                        }`}
                      >
                        {step.actionLabel || 'Configure'}
                      </button>
                    )}

                    {isCompleted && step.editLabel && (
                      <button
                        onClick={() => handleStepClick(step)}
                        className="text-sm text-blue-600 hover:text-blue-300 font-medium"
                      >
                        {step.editLabel || 'Edit'}
                      </button>
                    )}
                  </div>

                  {/* Expanded content */}
                  {isExpanded && step.content && (
                    <div className="mt-3 p-3 bg-muted rounded-lg text-sm text-ink-muted">
                      {step.content}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Compact inline progress indicator
 */
export const SetupProgress = ({ steps, onClick }) => {
  const completedCount = steps.filter(s => s.completed).length;
  const totalCount = steps.length;
  const allComplete = completedCount === totalCount;
  const nextStep = steps.find(s => !s.completed);

  if (allComplete) {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-500/10 text-green-400 rounded-full text-sm">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        <span className="font-medium">Setup Complete</span>
      </div>
    );
  }

  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 text-amber-400 rounded-full text-sm hover:bg-amber-500/15 transition-colors"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <span className="font-medium">Setup: {completedCount}/{totalCount}</span>
      {nextStep && (
        <span className="text-amber-600">→ {nextStep.title}</span>
      )}
    </button>
  );
};

export default SetupStepper;
